import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RelationshipsWithoutScreens = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <Badge className="mb-4">Digital Wellness</Badge>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              Relationships Without Screens – How Technology Changed Our Meetings
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>June 28, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>7 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Piotr</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4 mr-2" />
                Share Article
              </Button>
            </div>
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src="/lovable-uploads/5b506b4d-137c-47a7-bdb0-a29725093fbd.png" 
              alt="Hand holding notebook with 'Let's wander where the Wi-Fi is weak' message in forest setting"
              className="w-full h-[400px] md:h-[500px] object-cover grayscale"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="text-xl leading-relaxed text-muted-foreground mb-8 font-light italic">
              Imagine sitting at a dinner table where no phones are present. People look into each other's eyes, conversations flow without interruptions, and laughter fills the air. In today's world, such moments feel increasingly rare. Technology has brought us closer across distances but, paradoxically, it has pulled us apart in the moments that matter most.
            </div>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The impact of screens on relationships</h2>

              <p className="mb-6 leading-relaxed">
                Research shows that even the mere presence of a phone on the table – unused – reduces the depth of conversations and the feeling of closeness between people (Przybylski & Weinstein, 2013). Smartphones compete for our attention, signaling that at any moment, we might be distracted. This subtle dynamic undermines trust and intimacy.
              </p>

              <p className="mb-6 leading-relaxed">
                According to the American Psychological Association, <strong className="text-accent">more than 70% of adults report feeling distracted by their phones during social interactions</strong>, and many couples cite screen time as a source of conflict (APA, 2018).
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Why face-to-face matters</h2>

              <p className="mb-6 leading-relaxed">
                Human connection thrives on presence. Non-verbal cues – tone of voice, facial expression, touch – make up the majority of communication. When screens intrude, these subtle signals are weakened. Studies in social psychology demonstrate that <strong className="text-accent">meaningful offline interactions are strongly linked to higher levels of happiness and lower levels of loneliness</strong> (Holt-Lunstad et al., 2010).
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Practical ways to reclaim connection</h2>

              <ul className="space-y-3 ml-6 mb-6">
                <li><strong className="text-accent">Establish "phone-free zones"</strong> during meals or social gatherings.</li>
                <li><strong className="text-accent">Practice active listening</strong> – putting devices away and focusing on the other person.</li>
                <li><strong className="text-accent">Prioritize in-person meetings</strong> for important conversations.</li>
                <li><strong className="text-accent">Create shared rituals</strong> – like walking together or cooking – that naturally exclude screens.</li>
              </ul>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Conclusion</h2>

              <p className="mb-6 leading-relaxed">
                Technology is not the enemy. It's how we use it that matters. By setting boundaries and creating space for undistracted presence, we can rebuild the depth of our relationships. True connection doesn't happen through screens – it happens when we put them down.
              </p>

              <p className="text-xl font-light italic text-accent mb-6">
                Let's wander where the Wi-Fi is weak, but human connection is strong.
              </p>

              <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>• Przybylski, A. K., & Weinstein, N. (2013). Can you connect with me now? How the presence of mobile communication technology influences face-to-face conversation quality. <em>Journal of Social and Personal Relationships, 30</em>(3), 237–246.</p>
                  <p>• American Psychological Association. (2018). Stress in America: Generation Z.</p>
                  <p>• Holt-Lunstad, J., Smith, T. B., & Layton, J. B. (2010). Social relationships and mortality risk: A meta-analytic review. <em>PLoS Medicine, 7</em>(7), e1000316.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default RelationshipsWithoutScreens;